import React, { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { allSpecies } from '../../data/speciesData';
import MapControls from './MapControls';

const mapStyles = {
  dark: 'mapbox://styles/mapbox/dark-v11',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
};

const MapView = ({ selectedSpecies }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const animatedMarkerRef = useRef(null);
  const selectedSpeciesRef = useRef(selectedSpecies);
  const dashOffsetRef = useRef(0);
  const glowAnimRef = useRef(null);
  const [mapStyle, setMapStyle] = useState('satellite');

  useEffect(() => {
    selectedSpeciesRef.current = selectedSpecies;
  }, [selectedSpecies]);

  // Initialize map
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyles.satellite,
      center: [0, 20],
      zoom: 1.5,
      projection: 'globe',
      attributionControl: false,
      fadeDuration: 0,
      maxTileCacheSize: 100,
    });

    map.current.on('error', (e) => {
      console.error('Mapbox error:', e.error?.message || e);
    });

    map.current.on('load', () => {
      map.current.setFog({
        'space-color': '#060e20',
        'star-intensity': 0.6
      });
    });

    map.current.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    return () => {
      if (glowAnimRef.current) cancelAnimationFrame(glowAnimRef.current);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Draw route with animated dashes and styled popups
  const drawRoute = useCallback((speciesData) => {
    if (!map.current || !speciesData) return;

    // Clear existing
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    if (animatedMarkerRef.current) {
      animatedMarkerRef.current.remove();
      animatedMarkerRef.current = null;
    }
    if (glowAnimRef.current) {
      cancelAnimationFrame(glowAnimRef.current);
      glowAnimRef.current = null;
    }

    if (map.current.getLayer('route-line')) map.current.removeLayer('route-line');
    if (map.current.getLayer('route-glow')) map.current.removeLayer('route-glow');
    if (map.current.getSource('route')) map.current.removeSource('route');

    const routeCoordinates = speciesData.route.map(p => [p.lng, p.lat]);
    const routeGeoJSON = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: routeCoordinates }
    };

    map.current.addSource('route', { type: 'geojson', data: routeGeoJSON });

    // Glow layer with pulsing opacity
    map.current.addLayer({
      id: 'route-glow',
      type: 'line',
      source: 'route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': speciesData.color,
        'line-width': 12,
        'line-opacity': 0.3,
        'line-blur': 6
      }
    });

    // Animated route line
    map.current.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': speciesData.color,
        'line-width': 3,
        'line-opacity': 0.9,
        'line-dasharray': [2, 2]
      }
    });

    // Animate the dash offset and glow pulse
    let step = 0;
    const animateDash = () => {
      step += 1;
      const dashPhase = (step * 0.05) % 7;
      const glowOpacity = 0.3 + Math.sin(step * 0.008) * 0.1;

      try {
        if (map.current && map.current.getLayer('route-line')) {
          map.current.setPaintProperty('route-glow', 'line-opacity', glowOpacity);
        }
      } catch (e) {
        // Layer may have been removed during species switch
      }
      glowAnimRef.current = requestAnimationFrame(animateDash);
    };
    glowAnimRef.current = requestAnimationFrame(animateDash);

    // Styled waypoint markers with popups
    const totalStops = speciesData.route.length;
    speciesData.route.forEach((point, i) => {
      const isEndpoint = i === 0 || i === totalStops - 1;
      const el = document.createElement('div');
      el.style.cssText = `
        width: ${isEndpoint ? '24px' : '16px'};
        height: ${isEndpoint ? '24px' : '16px'};
        background-color: ${speciesData.color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 0 10px ${speciesData.color}80, 0 2px 6px rgba(0,0,0,0.3);
        cursor: pointer;
      `;

      const popupHTML = `
        <div class="fjp-popup">
          <div class="fjp-popup-accent" style="background:${speciesData.color}"></div>
          <div class="fjp-popup-label">Stop ${i + 1} of ${totalStops}</div>
          <div class="fjp-popup-name">${point.name}</div>
        </div>
      `;

      const marker = new mapboxgl.Marker(el)
        .setLngLat([point.lng, point.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25, closeButton: false, className: 'fjp-popup-container' })
            .setHTML(popupHTML)
        )
        .addTo(map.current);

      markersRef.current.push(marker);
    });

    // Animated species marker (photo or emoji fallback)
    const animatedEl = document.createElement('div');
    if (speciesData.image) {
      animatedEl.innerHTML = `
        <div style="
          width: 44px; height: 44px;
          border: 3px solid white;
          border-radius: 50%;
          overflow: hidden;
          box-shadow: 0 0 20px ${speciesData.color}80, 0 4px 12px rgba(0,0,0,0.4);
          animation: pulse 2s ease-in-out infinite;
        ">
          <img src="${speciesData.image}" alt="${speciesData.name}" style="width:100%;height:100%;object-fit:cover;" />
        </div>
      `;
    } else {
      animatedEl.innerHTML = `
        <div style="
          width: 40px; height: 40px;
          background: ${speciesData.color};
          border: 3px solid white;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px;
          box-shadow: 0 0 20px ${speciesData.color}80, 0 4px 12px rgba(0,0,0,0.4);
          animation: pulse 2s ease-in-out infinite;
        ">
          ${speciesData.icon}
        </div>
      `;
    }

    animatedMarkerRef.current = new mapboxgl.Marker(animatedEl)
      .setLngLat(routeCoordinates[0])
      .addTo(map.current);
  }, []);

  // Handle style toggle
  const handleStyleToggle = useCallback(() => {
    const newStyle = mapStyle === 'dark' ? 'satellite' : 'dark';
    setMapStyle(newStyle);
    if (map.current) {
      map.current.once('style.load', () => {
        map.current.setFog({
          'space-color': '#060e20',
          'star-intensity': 0.6
        });
        // Redraw route after style change
        const currentSpeciesId = selectedSpeciesRef.current;
        if (currentSpeciesId) {
          const speciesData = allSpecies.find(s => s.id === currentSpeciesId);
          if (speciesData) drawRoute(speciesData);
        }
      });
      map.current.setStyle(mapStyles[newStyle]);
    }
  }, [mapStyle, drawRoute]);

  const species = selectedSpecies ? allSpecies.find(s => s.id === selectedSpecies) : null;

  useEffect(() => {
    if (!map.current) return;

    const updateRoute = () => {
      if (!species) {
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        if (animatedMarkerRef.current) {
          animatedMarkerRef.current.remove();
          animatedMarkerRef.current = null;
        }
        if (glowAnimRef.current) {
          cancelAnimationFrame(glowAnimRef.current);
          glowAnimRef.current = null;
        }
        if (map.current.getLayer('route-line')) map.current.removeLayer('route-line');
        if (map.current.getLayer('route-glow')) map.current.removeLayer('route-glow');
        if (map.current.getSource('route')) map.current.removeSource('route');
        return;
      }

      drawRoute(species);

      const routeCoordinates = species.route.map(p => [p.lng, p.lat]);
      const bounds = routeCoordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(routeCoordinates[0], routeCoordinates[0]));

      map.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 400, right: 300 },
        duration: 1000
      });
    };

    if (map.current.isStyleLoaded()) {
      updateRoute();
    } else {
      map.current.once('style.load', updateRoute);
    }
  }, [species, drawRoute]);

  // Animate species marker along route — distance-proportional speed
  useEffect(() => {
    if (!species) return;

    const routeCoordinates = species.route.map(p => [p.lng, p.lat]);
    if (routeCoordinates.length < 2) return;

    // Calculate cumulative distances for each segment
    const segmentDistances = [];
    let totalDistance = 0;
    for (let i = 0; i < routeCoordinates.length - 1; i++) {
      const [lng1, lat1] = routeCoordinates[i];
      const [lng2, lat2] = routeCoordinates[i + 1];
      const dist = Math.sqrt((lng2 - lng1) ** 2 + (lat2 - lat1) ** 2);
      segmentDistances.push(dist);
      totalDistance += dist;
    }

    // Build cumulative distance breakpoints (0 to 1)
    const cumulative = [0];
    let running = 0;
    for (const dist of segmentDistances) {
      running += dist;
      cumulative.push(running / totalDistance);
    }

    let progress = 0;

    const interval = setInterval(() => {
      progress = (progress + 0.08) % 100;
      const t = progress / 100; // 0 to 1 along total distance

      // Find which segment we're in
      let segmentIndex = 0;
      for (let i = 0; i < cumulative.length - 1; i++) {
        if (t >= cumulative[i] && t < cumulative[i + 1]) {
          segmentIndex = i;
          break;
        }
      }

      // How far through this segment (0 to 1)
      const segStart = cumulative[segmentIndex];
      const segEnd = cumulative[segmentIndex + 1];
      const segmentProgress = segEnd > segStart ? (t - segStart) / (segEnd - segStart) : 0;

      if (segmentIndex < routeCoordinates.length - 1 && animatedMarkerRef.current) {
        const start = routeCoordinates[segmentIndex];
        const end = routeCoordinates[segmentIndex + 1];
        const lng = start[0] + (end[0] - start[0]) * segmentProgress;
        const lat = start[1] + (end[1] - start[1]) * segmentProgress;
        animatedMarkerRef.current.setLngLat([lng, lat]);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [species]);

  const handleZoomIn = () => map.current?.zoomIn({ duration: 300 });
  const handleZoomOut = () => map.current?.zoomOut({ duration: 300 });
  const handleReset = () => {
    map.current?.flyTo({ center: [0, 20], zoom: 1.5, duration: 1000 });
  };

  return (
    <section className="absolute inset-0 z-0">
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        /* Field Journal styled popups */
        .fjp-popup-container .mapboxgl-popup-content {
          background: #222a3d;
          padding: 0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 12px 32px rgba(0,0,0,0.4);
          min-width: 160px;
        }
        .fjp-popup-container .mapboxgl-popup-tip {
          border-top-color: #222a3d;
        }
        .fjp-popup {
          padding: 0;
        }
        .fjp-popup-accent {
          height: 3px;
          width: 100%;
        }
        .fjp-popup-label {
          font-family: 'Work Sans', sans-serif;
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #a38c7e;
          padding: 10px 14px 0;
        }
        .fjp-popup-name {
          font-family: 'Noto Serif', serif;
          font-size: 14px;
          font-weight: 600;
          color: #dae2fd;
          padding: 4px 14px 12px;
        }
      `}</style>

      <div className="relative w-full h-full bg-surface-container-lowest">
        <div ref={mapContainer} className="w-full h-full" />
        <div className="absolute inset-0 map-gradient pointer-events-none"></div>
        <MapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onReset={handleReset}
          onStyleToggle={handleStyleToggle}
          currentStyle={mapStyle}
        />
      </div>
    </section>
  );
};

export default MapView;
